import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, ChevronDown, Paperclip, ArrowDownLeft, ArrowUpRight, Search, ExternalLink } from "lucide-react";
import type { DealEmailRecord } from "@/types/email";
import { groupEmailThreads, type EmailThread, formatEmailDate } from "@/pages/deals/DealEmailThread";

interface EmailsWidgetProps {
  emails: DealEmailRecord[];
  onCompose?: () => void;
  canSend?: boolean;
}

export function EmailsWidget({ emails, onCompose, canSend }: EmailsWidgetProps) {
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const threads = groupEmailThreads(emails);
  const totalUnread = emails.filter((e) => !e.is_read).length;

  const filteredThreads = threads.filter((t) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      t.subject.toLowerCase().includes(term) ||
      t.messages.some(
        (m) =>
          m.from_address.toLowerCase().includes(term) ||
          m.to_addresses.some((to) => to.toLowerCase().includes(term)) ||
          (m.snippet && m.snippet.toLowerCase().includes(term))
      )
    );
  });

  const toggleThread = (id: string) => {
    setExpandedThreadId((curr) => (curr === id ? null : id));
  };

  return (
    <div className="bg-card border border-border/80 rounded-2xl shadow-xs overflow-hidden">
      {/* Top Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-border/80">
        <div className="flex items-center gap-3">
          <div className="size-8 rounded-xl bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
            <Mail className="size-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">
                Email History ({emails.length})
              </h3>
              {totalUnread > 0 && (
                <Badge variant="default" className="text-[10px] px-1.5 py-0 bg-primary">
                  {totalUnread} unread
                </Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {threads.length} conversation {threads.length === 1 ? "thread" : "threads"}
            </p>
          </div>
        </div>

        {canSend && onCompose && (
          <Button
            type="button"
            size="sm"
            onClick={onCompose}
            className="gap-1.5 self-start sm:self-center h-8 text-xs font-medium"
          >
            <Mail className="size-3.5" />
            <span>Compose Email</span>
          </Button>
        )}
      </div>

      {/* Threads List */}
      {threads.length === 0 ? (
        <div className="p-8 text-center space-y-3">
          <div className="size-10 rounded-full bg-muted flex items-center justify-center mx-auto text-muted-foreground">
            <Mail className="size-5" />
          </div>
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No emails found</p>
            <p className="text-xs text-muted-foreground">
              No synced or sent emails recorded for this record yet.
            </p>
          </div>
          {canSend && onCompose && (
            <Button type="button" variant="outline" size="sm" onClick={onCompose} className="gap-1.5 text-xs">
              <Mail className="size-3.5" />
              <span>Send First Email</span>
            </Button>
          )}
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {/* Optional Search */}
          {threads.length > 5 && (
            <div className="p-3 bg-muted/20">
              <div className="relative">
                <Search className="size-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search email threads..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full text-xs pl-9 pr-3 py-1.5 rounded-lg border bg-background placeholder:text-muted-foreground/60"
                />
              </div>
            </div>
          )}

          {filteredThreads.map((thread) => {
            const isExpanded = expandedThreadId === thread.id;
            const last = thread.last;
            const isIncoming = last.direction === "IN";

            return (
              <div key={thread.id} className="transition-colors hover:bg-muted/30">
                <button
                  type="button"
                  onClick={() => toggleThread(thread.id)}
                  className="w-full text-left p-4 space-y-2 cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`p-1 rounded-md ${isIncoming ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-blue-500/10 text-blue-600 dark:text-blue-400"}`}>
                        {isIncoming ? <ArrowDownLeft className="size-3" /> : <ArrowUpRight className="size-3" />}
                      </span>
                      <span className={`text-xs text-foreground truncate ${thread.unread > 0 ? "font-bold" : "font-semibold"}`}>
                        {thread.subject}
                      </span>
                      {thread.messages.length > 1 && (
                        <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-muted text-muted-foreground font-mono shrink-0">
                          {thread.messages.length}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] text-muted-foreground font-mono">
                        {formatEmailDate(last.email_date)}
                      </span>
                      <ChevronDown
                        className={`size-3.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-180" : ""}`}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <p className="truncate max-w-[85%]">
                      <span className="font-medium text-foreground/80">
                        {isIncoming ? (last.from_name || last.from_address) : `To: ${last.to_addresses.join(", ")}`}:
                      </span>{" "}
                      <span>{last.snippet || "(No preview)"}</span>
                    </p>
                    {last.attachments && last.attachments.length > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground font-mono shrink-0">
                        <Paperclip className="size-3" />
                        {last.attachments.length}
                      </span>
                    )}
                  </div>
                </button>

                {/* Expanded Thread Messages */}
                {isExpanded && (
                  <div className="px-5 pb-4 pt-1 space-y-3 bg-muted/20 border-t border-border/40">
                    {thread.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className="bg-card border border-border/70 rounded-xl p-3.5 space-y-2.5 shadow-2xs text-xs"
                      >
                        <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-2">
                          <div className="space-y-0.5">
                            <div className="font-semibold text-foreground">
                              {msg.from_name ? `${msg.from_name} <${msg.from_address}>` : msg.from_address}
                            </div>
                            <div className="text-[11px] text-muted-foreground">
                              To: {msg.to_addresses.join(", ")}
                              {msg.cc_addresses && msg.cc_addresses.length > 0 && ` • CC: ${msg.cc_addresses.join(", ")}`}
                            </div>
                          </div>
                          <span className="text-[11px] text-muted-foreground font-mono shrink-0">
                            {formatEmailDate(msg.email_date)}
                          </span>
                        </div>

                        {/* Body content */}
                        <div className="text-foreground/90 leading-relaxed max-h-96 overflow-y-auto whitespace-pre-wrap font-sans text-xs">
                          {msg.body_text || msg.snippet || "(Empty body)"}
                        </div>

                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="pt-2 border-t border-border/40 flex flex-wrap gap-2">
                            {msg.attachments.map((att, aIdx) => (
                              <div
                                key={aIdx}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted text-[11px] font-medium text-foreground border border-border/60"
                              >
                                <Paperclip className="size-3 text-muted-foreground" />
                                <span className="truncate max-w-[150px]">{att.file_name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
